import { Link } from "@inertiajs/react"
import { ChevronRight, Gift } from "lucide-react"
import { useTranslation } from "react-i18next"

import { RewardTeaserBar } from "@/components/rewards/reward-teaser-bar"
import { Card, CardContent } from "@/components/ui/card"
import { creatorRewardsPath } from "@/routes"
import type { RewardTeaser } from "@/types/xp"

interface RewardTeaserCardProps {
  /** The creator's spendable coin balance (progress-bar denominator input). */
  coinBalance: number
  /** Nearest not-yet-affordable reward, or null → nothing to tease. */
  rewardTeaser: RewardTeaser | null
}

/**
 * OKS-765 — "Next reward to unlock" teaser on the creator Missions home
 * (`/missions`), above the mission list. A gold reward card (the coins/reward
 * visual language of the Profile §11 wallet) reading a `Gift` icon + the
 * "Next reward to unlock" label + the shared `RewardTeaserBar` (progress +
 * "{N} coins from the {reward}"), tappable as a whole-card `Link` to the
 * rewards catalog (`creatorRewardsPath()`, js-routes).
 *
 * Renders NOTHING when `rewardTeaser` is null — the brand has no redeemable
 * reward, or the creator already affords every one (UX Spec §3: no empty
 * shell, no placeholder). The teaser resolves synchronously from the page
 * props, so it has no independent loading/error state and persists above the
 * mission list's own retry (UX Spec §3).
 */
export function RewardTeaserCard({
  coinBalance,
  rewardTeaser,
}: RewardTeaserCardProps) {
  const { t } = useTranslation()

  if (!rewardTeaser) return null

  return (
    <Link
      href={creatorRewardsPath()}
      data-testid="missions-reward-teaser"
      className="group focus-visible:ring-ring block rounded-xl focus-visible:ring-2 focus-visible:outline-none"
    >
      <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-amber-100/40 transition-colors group-hover:from-amber-100 group-active:from-amber-100 dark:border-amber-900/40 dark:from-amber-950/30 dark:to-amber-900/10">
        <CardContent className="flex items-center gap-4 py-5">
          <Gift aria-hidden="true" className="size-8 shrink-0 text-amber-600" />
          <div className="flex flex-1 flex-col gap-1.5 overflow-hidden">
            <span className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
              {t("creator_home.reward_teaser.label")}
            </span>
            <RewardTeaserBar
              coinBalance={coinBalance}
              rewardTeaser={rewardTeaser}
              truncate
            />
          </div>
          <ChevronRight
            aria-hidden="true"
            className="text-muted-foreground size-5 shrink-0"
          />
        </CardContent>
      </Card>
    </Link>
  )
}
